/**
 * The per-component switches that live under the `INVENTORY` module.
 *
 * The module's BASE is the SKU catalog — turning INVENTORY on gets you products
 * and nothing else. That is a useful state on its own: a SKU is the natural key
 * for mapping products to an outside system, so a shop can keep a clean product
 * master without ever tracking a package.
 *
 * Everything physical hangs off `stockTracking` — items, movements, locations,
 * boxes. Every other component reads or writes stock, so they all imply it; see
 * {@link STOCK_DEPENDENT} and {@link readInventoryComponents}.
 *
 * Keep in sync with `InventorySettings` in
 * `packages/api/src/models/vendor-modules.ts`.
 */
export type InventoryComponentKey =
  | 'stockTracking'
  | 'packingStation'
  | 'orders'
  | 'furtherProcessing'
  | 'labelsAndPrinting'
  | 'scanningAndStocktake'
  | 'sourcingAndCosting'
  | 'recallAndTraceability'
  | 'wholesaleOrders'
  | 'wholesaleHoldsStock';

export interface InventoryComponentMeta {
  key: InventoryComponentKey;
  /** Short name, as shown in the admin module form. */
  label: string;
  /** What turning it on actually gives the vendor. */
  description: string;
}

/** The components, in the order the admin form shows them. */
export const INVENTORY_COMPONENTS: InventoryComponentMeta[] = [
  {
    key: 'stockTracking',
    label: 'Stock tracking',
    description:
      'Physical inventory: packages, movements, locations and boxes. Everything below needs it — without it the module is a product catalog only.',
  },
  {
    key: 'packingStation',
    label: 'Packing station',
    description:
      'Pack-as-you-go screen and the Pack entry point on the job page.',
  },
  {
    key: 'orders',
    label: 'Orders & fulfillment',
    description:
      'Fulfillment orders, lifecycle, per-order pickup and invoice link.',
  },
  {
    key: 'furtherProcessing',
    label: 'Further processing',
    description:
      'Work orders for further processing, and the batches that pool them.',
  },
  {
    key: 'labelsAndPrinting',
    label: 'Labels & printing',
    description:
      'Label templates, the layout editor, print stations and label printing.',
  },
  {
    key: 'scanningAndStocktake',
    label: 'Scanning & stocktake',
    description: 'Barcode scan stations and stocktake sessions.',
  },
  {
    key: 'sourcingAndCosting',
    label: 'Sourcing & costing',
    description:
      'Purchase orders, suppliers, inbound stock and per-animal costing.',
  },
  {
    key: 'recallAndTraceability',
    label: 'Recall & traceability',
    description: 'Recall runs and the trace built on the movement ledger.',
  },
  {
    key: 'wholesaleOrders',
    label: 'Wholesale orders',
    description:
      'Phone-entered trade orders: pull sheets, weigh-then-price at $/lb, invoices and check payments. Works without stock tracking — opens the Orders page on its own.',
  },
  {
    key: 'wholesaleHoldsStock',
    label: 'Wholesale holds stock',
    description:
      'For plants that fill wholesale from the shelf: a confirmed wholesale order holds its stock (so the counter can’t sell it) and takes it when packed. Leave off for plants that cut wholesale to order. Needs stock tracking and wholesale orders.',
  },
];

/**
 * Components that cannot function without {@link InventoryComponentKey}
 * `stockTracking`, because each one creates, consumes or traces physical
 * packages. Labels are the one thing that stands alone: a station prints
 * documents that have nothing to do with stock.
 */
export const STOCK_DEPENDENT: InventoryComponentKey[] = [
  'packingStation',
  'orders',
  'furtherProcessing',
  'scanningAndStocktake',
  'sourcingAndCosting',
  'recallAndTraceability',
  'wholesaleHoldsStock',
];

export type InventoryComponentFlags = Record<InventoryComponentKey, boolean>;

/** Every component off — the shape returned when the module itself is off. */
export const NO_INVENTORY_COMPONENTS: InventoryComponentFlags =
  Object.fromEntries(
    INVENTORY_COMPONENTS.map(({ key }) => [key, false]),
  ) as InventoryComponentFlags;

/**
 * Read the module's settings blob into a complete set of flags.
 *
 * Absent means off. A vendor switched on before a component existed — or one
 * enabled by a script that wrote only `active` — gets nothing they weren't
 * explicitly given.
 * @param settings The `settings` JSON from the vendor's INVENTORY module row.
 * @returns One boolean per component.
 */
export function readInventoryComponents(
  settings: unknown,
): InventoryComponentFlags {
  const raw = (settings ?? {}) as Record<string, unknown>;

  const flags = Object.fromEntries(
    INVENTORY_COMPONENTS.map(({ key }) => [key, raw[key] === true]),
  ) as InventoryComponentFlags;

  // Stock-dependent components are forced off without stock tracking, rather
  // than trusted from the settings blob. Each of them creates, consumes or
  // traces physical packages, so switched on alone they render a screen with
  // nowhere to put anything — a packing station that can't produce a package,
  // a recall trace over a movement ledger that isn't being written. Reading the
  // dependency here means every caller gets it, instead of each screen
  // remembering to check two flags.
  if (!flags.stockTracking) {
    for (const key of STOCK_DEPENDENT) flags[key] = false;
  }

  return flags;
}

/**
 * Read the TEMPORARY inventory-beta switch from the module's settings blob:
 * whether this processor's users get the floating Feedback button. Not a
 * component — it sits beside them in the same blob and nothing depends on it.
 *
 * Absent (or anything but `true`) means off.
 * @param settings The `settings` JSON from the vendor's INVENTORY module row.
 * @returns Whether the beta feedback button is switched on.
 */
export function readInventoryBetaFeedback(settings: unknown): boolean {
  return (settings as { betaFeedback?: unknown } | null)?.betaFeedback === true;
}
